import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Check, HandCoins, X } from "lucide-react";
import { useData } from "@/context/DataContext";
import { useConfirm } from "@/context/ConfirmContext";
import { LOAN_MSG } from "@/data/constants";
import { money, today } from "@/lib/money";
import { t } from "@/lib/i18n";
import Expander from "@/components/mm/Expander";

const daysLeft = (d) => Math.ceil((new Date(d + "T00:00:00") - new Date(new Date().toDateString())) / 864e5);

function Status({ l }) {
  if (l.ret) return <span className="text-[var(--ok)]">Returned {l.ret}</span>;
  if (!l.return_date) return <span>Not returned yet</span>;
  const d = daysLeft(l.return_date);
  return <b style={{ color: d <= 0 ? "var(--dg)" : "var(--tx)" }}>{d < 0 ? `${-d} days overdue` : d === 0 ? "Due today" : `${d} days left`}</b>;
}

export default function Borrowed({ contentOnly = false }) {
  const { db, create, remove, patchLocal } = useData();
  const confirm = useConfirm();
  const [f, setF] = useState({ who: "", what: "", amt: "", return_date: "" });
  const [returning, setReturning] = useState(null);
  const [msg] = useState(() => LOAN_MSG[Math.floor(Math.random() * LOAN_MSG.length)]);
  const list = [...db.loans].sort((a, b) => (a.ret ? 1 : 0) - (b.ret ? 1 : 0) || b.created_at - a.created_at);
  const owed = db.loans.filter((l) => !l.ret).reduce((t, l) => t + (l.amt || 0), 0);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const add = async (e) => {
    e.preventDefault();
    if (!f.who.trim() || !f.what.trim()) return;
    try {
      await create("loans", { who: f.who.trim(), what: f.what.trim(), amt: parseFloat(f.amt) || 0, date: today(), return_date: f.return_date, ret: null });
      setF({ who: "", what: "", amt: "", return_date: "" });
    } catch { /* Keep the form so the user can retry. */ }
  };
  const markReturned = async (loan) => {
    if (returning === loan.id || loan.ret) return;
    setReturning(loan.id);
    try {
      const { data } = await api.post(`/loans/${encodeURIComponent(loan.id)}/return`, { date: today() });
      patchLocal("loans", (rows) => rows.map((x) => x.id === loan.id ? data.loan : x));
      if (data.expense) patchLocal("expenses", (rows) => rows.some((x) => x.id === data.expense.id) ? rows : [...rows, data.expense]);
      toast.success("Borrowed item marked returned and repayment recorded.");
    } catch (error) { toast.error(errMsg(error)); }
    finally { setReturning(null); }
  };
  const content = (
    <>
      <form onSubmit={add} className="grid grid-cols-2 gap-2">
        <input className="mm-input" placeholder="From whom" maxLength={80} value={f.who} onChange={set("who")} required data-testid="borrowed-who-input" />
        <input className="mm-input" placeholder="What (e.g. money, book)" maxLength={120} value={f.what} onChange={set("what")} required data-testid="borrowed-what-input" />
        <input className="mm-input num" type="number" step="0.01" min="0" placeholder="Amount (optional)" value={f.amt} onChange={set("amt")} data-testid="borrowed-amount-input" />
        <input className="mm-input" type="date" title="Return by (optional)" value={f.return_date} onChange={set("return_date")} data-testid="borrowed-return-date-input" />
        <button className="mm-btn col-span-2 justify-center" data-testid="borrowed-add-button">Add</button>
      </form>
      <div className="mt-4 space-y-2">
        {!list.some((l) => !l.ret) && <p className="text-sm text-[var(--mu)]">{msg}</p>}
        {list.map((l) => (
          <div key={l.id} className={`flex items-center gap-2 rounded-[var(--ri)] border border-[var(--bd)] p-3 ${l.ret ? "opacity-60" : ""}`} data-testid={`borrowed-item-${l.id}`}>
            <div className="min-w-0 flex-1 text-sm">
              <div className="truncate font-semibold">{l.what}{l.amt ? ` · ${money(l.amt)}` : ""} <span className="font-normal text-[var(--mu)]">from {l.who}</span></div>
              <div className="text-xs text-[var(--mu)]">Borrowed {l.date} · <Status l={l} /></div>
            </div>
            {!l.ret && <button className="mm-btn sm" disabled={returning === l.id} onClick={() => markReturned(l).catch(() => {})} data-testid={`borrowed-return-${l.id}`}><Check className="h-3.5 w-3.5" /> {returning === l.id ? "Saving…" : "Returned"}</button>}
            <button className="grid h-8 w-8 place-items-center rounded-full text-[var(--dg)] hover:bg-[var(--bg)]" onClick={async () => { if (await confirm("Delete this entry?", { danger: true, yes: "Delete" })) remove("loans", l.id).catch(() => {}); }} aria-label="Delete" data-testid={`borrowed-delete-${l.id}`}><X className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </>
  );
  return contentOnly ? content : <Expander tile icon={HandCoins} title={t("Borrowed items")} testId="borrowed-items-widget" badge={owed > 0 && <span className="num text-sm font-bold text-[var(--dg)]" data-testid="borrowed-total">{money(owed)} {t("owed")}</span>}>{content}</Expander>;
}
